import React from 'react';
import { CONTACT_EMAIL } from '../../constants';

export const INVESTOR_DOCS = [
  { href: '/investors', label: 'Overview' },
  { href: '/investors/vision', label: 'Vision' },
  { href: '/investors/deep-dive', label: 'Deep dive' },
] as const;

export const InvestorsSubnav: React.FC<{ active: string }> = ({ active }) => (
  <nav aria-label="Investor documents" className="flex flex-wrap items-center gap-2">
    {INVESTOR_DOCS.map((doc) => (
      <a
        key={doc.href}
        href={doc.href}
        aria-current={active === doc.href ? 'page' : undefined}
        className={
          active === doc.href
            ? 'rounded-full bg-sky-400 px-4 py-1.5 text-xs font-bold text-black'
            : 'rounded-full border border-white/12 bg-white/[0.04] px-4 py-1.5 text-xs font-semibold text-white/70 transition hover:border-white/25 hover:text-white'
        }
      >
        {doc.label}
      </a>
    ))}
  </nav>
);

export const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">{children}</p>
);

export const DocSection: React.FC<{ id: string; num?: string; title: string; children: React.ReactNode }> = ({ id, num, title, children }) => (
  <section id={id} className="scroll-mt-28 border-t border-white/10 pt-10">
    <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
      {num ? <span className="mr-3 text-white/30">{num}</span> : null}
      {title}
    </h2>
    <div className="mt-5 space-y-4 text-[15px] leading-8 text-white/72">{children}</div>
  </section>
);

export const DocH3: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="pt-4 font-display text-lg font-bold tracking-tight text-white">{children}</h3>
);

export const Callout: React.FC<{ label?: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="rounded-2xl border border-sky-400/25 bg-sky-400/[0.06] p-5 text-[15px] leading-7 text-white/80">
    {label ? <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-sky-400">{label}</p> : null}
    {children}
  </div>
);

export const FactGrid: React.FC<{ items: { k: string; v: string }[] }> = ({ items }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
    {items.map((it) => (
      <div key={it.k} className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
        <p className="font-display text-xl font-bold text-white">{it.v}</p>
        <p className="mt-1 text-xs leading-5 text-white/55">{it.k}</p>
      </div>
    ))}
  </div>
);

export const DocTable: React.FC<{ head: string[]; rows: string[][] }> = ({ head, rows }) => (
  <div className="overflow-x-auto rounded-2xl border border-white/10">
    <table className="w-full min-w-[560px] text-left text-sm">
      <thead>
        <tr className="border-b border-white/10 bg-white/[0.04]">
          {head.map((h) => (
            <th key={h} className="px-4 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white/55">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-white/5 last:border-0">
            {r.map((c, j) => (
              <td key={j} className={`px-4 py-3 leading-6 ${j === 0 ? 'font-semibold text-white' : 'text-white/68'}`}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const InvestorsContactCta: React.FC = () => (
  <section className="border-t border-white/10 py-14">
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center sm:px-12">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.14),transparent_60%)]" />
      <div className="relative">
        <h2 className="font-display text-2xl font-bold tracking-tight sm:text-4xl">Talk to us</h2>
        <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-8 text-white/70">
          Try the product, then email the founder. Financials and cap table are shared under NDA.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Investor conversation, app.nz')}`} className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-6 text-sm font-bold text-black transition hover:bg-sky-300">
            Email the founder
          </a>
          <a href="/signup" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
            Try the product
          </a>
        </div>
      </div>
    </div>
  </section>
);
